import { useCallback, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { authApi, refreshSession } from '../api/client.js';
import { clearSession, setSession } from '../api/session-store.js';
import type { PublicUser } from '../api/types.js';
import { useI18n } from '../i18n/context.js';
import { AuthContext, type AuthState } from './context.js';

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { setLocale, resolveLocale } = useI18n();
  const [user, setUserState] = useState<PublicUser | null>(null);
  const [isBootstrapping, setBootstrapping] = useState(true);
  const [isReconnecting, setReconnecting] = useState(false);

  /**
   * Восстановление сессии при загрузке страницы. Access-токен живёт в памяти
   * и после перезагрузки теряется, а refresh лежит в httpOnly-cookie — поэтому
   * «обновление страницы не разлогинивает» держится именно на этом запросе.
   */
  useEffect(() => {
    let cancelled = false;

    const restore = async (): Promise<void> => {
      let retryMs = 250;

      while (!cancelled) {
        try {
          const session = await refreshSession();
          if (cancelled) return;
          setUserState(session?.user ?? null);
          setReconnecting(false);
          setBootstrapping(false);
          if (session?.user) setLocale(resolveLocale(session.user.locale));
          return;
        } catch {
          if (cancelled) return;
          setReconnecting(true);
          await delay(retryMs);
          retryMs = Math.min(retryMs * 2, 5_000);
        }
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
  }, [resolveLocale, setLocale]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const session = await authApi.login({ email, password });
      setSession(session.accessToken, session.user);
      setUserState(session.user);
      setLocale(resolveLocale(session.user.locale));
    },
    [resolveLocale, setLocale],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      // Даже если сервер ответил ошибкой, локально сессии больше нет.
      clearSession();
      setUserState(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, isBootstrapping, isReconnecting, login, logout, setUser: setUserState }),
    [user, isBootstrapping, isReconnecting, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
